import {
  forwardRef,
  ReactElement,
  Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import useTranslations from "../../../core/i18n/useTranslations";
import { BatchSMSModal } from "../../../components/Object/BatchSMSModal";
import Button from "../Button/Button";
import Dropdown from "../Dropdown/Dropdown";
import { Row } from "../../../components/Layout/Layout";
import { IDropdownItem } from "../Dropdown/DropdownItem";
import {
  BaseTableDataParams,
  BatchAllTableDataParams,
} from "../../../core/api/common_table";
import { deleteAllSubscriptions } from "../../../core/api/objects/objects";
import useUser from "../../../core/user/useUser";
import { notify } from "../Alerts/Toast";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import PrimeModal from "../PrimeModal/PrimeModal";
import Icon from "../Icon";

import styles from "./TableRowSelectionPopup.module.css";

export declare type AllTableRowSelectionPopupActions<_TRowData> = {
  open: (selectedItems: number) => void;
  close: (clear?: boolean) => void;
  toggle: () => void;
};

export interface AllTableRowSelectionPopupProps<_TRowData> {
  onClose: (text?: any) => void;
  tableParams: BaseTableDataParams;
}

const AllTableRowSelectionPopupInternal = <TRowData,>(
  { onClose, tableParams }: AllTableRowSelectionPopupProps<TRowData>,
  ref: Ref<AllTableRowSelectionPopupActions<TRowData>>,
) => {
  const { authenticatedRequest } = useUser();
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number>(0);
  const [selectedRowAction, setSelectedRowAction] = useState<number>(1);
  const [userTable, setUserTable] = useState<
    BatchAllTableDataParams | undefined
  >();

  const rowSelectionActionItems: IDropdownItem[] = [
    {
      id: 1,
      name: t("Objects:dropdown_cancel_subscription"),
      isSelected: selectedRowAction === 1,
    },
    {
      id: 2,
      name: t("Objects:dropdown_batch_sms"),
      isSelected: selectedRowAction === 2,
    },
  ];

  useImperativeHandle<
    AllTableRowSelectionPopupActions<TRowData>,
    AllTableRowSelectionPopupActions<TRowData>
  >(ref, () => {
    const actions: AllTableRowSelectionPopupActions<TRowData> = {
      open: function open(selectedItemsT) {
        if (selectedItemsT === 0) {
          setIsOpen(false);
          setSelectedItems(0);
        } else {
          setIsOpen(true);
          setSelectedItems(selectedItemsT);
        }
      },
      close: function close(clear?: boolean) {
        if (clear) {
          setSelectedItems(0);
        }
        setIsOpen(false);
      },
      toggle: function toggle() {
        setIsOpen(!isOpen);
      },
    };
    return actions;
  });

  useEffect(() => {
    if (tableParams) {
      const newUserTable = {
        selected: true,
        exact: false,
        nameonly: false,
        customers: tableParams.customers,
        start: tableParams.start,
        end: tableParams.end,
      } as any;
      (tableParams.filter || []).forEach((filter, index) => {
        newUserTable[`filterType${index === 0 ? "" : index + 1}`] = filter.id;
        newUserTable[`filterValue${index === 0 ? "" : index + 1}`] =
          filter.value;
      });
      if (tableParams.searchFilter!.value.length) {
        const defaultFilterLength = (tableParams.filter || []).length;
        if (tableParams.searchFilter!.id === -1) {
          newUserTable.q = tableParams.searchFilter!.value;
        } else {
          newUserTable[
            `filterType${
              defaultFilterLength === 0 ? "" : defaultFilterLength + 1
            }`
          ] = tableParams.searchFilter!.id;
          newUserTable[
            `filterValue${
              defaultFilterLength === 0 ? "" : defaultFilterLength + 1
            }`
          ] = tableParams.searchFilter!.value;
        }
      }
      setUserTable(newUserTable);
    }
  }, [tableParams]);

  return (
    <PrimeModal
      modal={false}
      isOpen={isOpen}
      onClose={onClose}
      position="bottom"
      fixedWidth="630px"
    >
      <div className={styles.popupHeader}>
        <Row align="center" type="space">
          {`${selectedItems} ${t("Common:items_selected")}`}
          <Icon name="x" size={22} onClick={onClose} />
        </Row>
      </div>
      <div className={styles.popupBody}>
        <Row type="space" align="center">
          <Dropdown
            items={rowSelectionActionItems}
            bodyWidth="fit-content"
            placeholder={t("Transmitters:select_action")}
            selectedItem={rowSelectionActionItems.find((r) => r.isSelected)}
            onSelectItem={(item) => {
              setSelectedRowAction(+item.id);
            }}
            width="230px"
            maxVisible={6}
          />

          {selectedRowAction === 1 && (
            <Button
              variant="primary"
              text={`${t("Objects:apply_to_selection")} (${selectedItems})`}
              loading={isLoading}
              disabled={isLoading}
              onClick={async () => {
                if (!userTable) return;
                setIsLoading(true);

                try {
                  await deleteAllSubscriptions(
                    selectedItems,
                    userTable,
                    authenticatedRequest,
                  );

                  notify({
                    message: t("Objects:all_subscription_cancel_success"),
                  });
                  onClose(t("Objects:batch_subscription_deletion"));
                } catch (error: any) {
                  notifyApiErrors(
                    error.response?.data?.errors,
                    error.response?.status,
                  );
                }
                setIsLoading(false);
              }}
            />
          )}
          {selectedRowAction === 2 && (
            <>
              <BatchSMSModal
                isOpen={isSmsModalOpen}
                onClose={(text?: string) => {
                  setIsSmsModalOpen(false);
                  onClose(text);
                }}
                ids={[]}
                tableData={[]}
                userTable={userTable}
                limit={selectedItems}
              />
              <Button
                variant="primary"
                text={`${t("Objects:apply_to_selection")} (${selectedItems})`}
                onClick={() => {
                  setIsSmsModalOpen(true);
                }}
              />
            </>
          )}
        </Row>
      </div>
    </PrimeModal>
  );
};

export const AllTableRowSelectionPopup = forwardRef(
  AllTableRowSelectionPopupInternal,
) as <TRowData>(
  p: AllTableRowSelectionPopupProps<TRowData> & {
    ref?: Ref<AllTableRowSelectionPopupActions<TRowData>>;
  },
) => ReactElement;

export default AllTableRowSelectionPopup;
